import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';

const Features = () => {
    const navigate = useNavigate();
    const [session, setSession] = useState(null);

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session);
        });

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
        });

        return () => subscription.unsubscribe();
    }, []);

    const handlePrimaryAction = () => {
        if (session) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    };

    const featuresList = [
        {
            icon: "🧊",
            title: "Interactive AR Menus",
            description: "Allow customers to see their food in 3D right on their table before ordering. Increases appetite and reduces order anxiety."
        },
        {
            icon: "📱",
            title: "Instant QR Access",
            description: "No app download required. Customers simply scan a QR code to view your stunning digital menu instantly."
        },
        {
            icon: "🧠",
            title: "Smart Upselling",
            description: "Manually configure perfect pairings (like drinks with mains) to automatically boost your ticket size."
        },
        {
            icon: "⭐",
            title: "Customer Belief & Ratings",
            description: "Collect verified reviews from diners after their meal. Display star ratings to build trust and highlight top dishes."
        },
        {
            icon: "👨‍🍳",
            title: "Kitchen Display System (KDS)",
            description: "Streamline your back-of-house operations with a real-time digital ticket screen. Track cooking times and order status."
        },
        {
            icon: "📊",
            title: "Real-time Analytics",
            description: "Track views, orders, and popular items. Make data-driven decisions to optimize your menu and pricing."
        },
        {
            icon: "🎨",
            title: "Custom Branding",
            description: "Your menu, your brand. Customize theme colors and logos to match your restaurant's unique identity."
        }
    ];

    return (
        <div className="bg-white min-h-screen font-sans text-gray-900">

            {/* NAVBAR */}
            <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur border-b border-gray-100">
                <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                        <img src="/favicon.png" alt="Logo" className="w-8 h-8 object-contain" />
                        <span className="text-xl font-bold tracking-tight">TableAR</span>
                    </div>
                    <div>
                        <button onClick={handlePrimaryAction} className="bg-black text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-gray-800 transition-colors">
                            {session ? "Go to Dashboard" : "Dashboard Login"}
                        </button>
                    </div>
                </div>
            </nav>

            {/* HERO HERO */}
            <header className="py-20 bg-gray-50 text-center px-4">
                <h1 className="text-4xl md:text-6xl font-black mb-6 max-w-4xl mx-auto leading-tight">
                    Everything You Need to <span className="text-[#ee5b2b]">Modernize Your Restaurant</span>
                </h1>
                <p className="text-xl text-gray-500 max-w-2xl mx-auto mb-10 leading-relaxed">
                    TableAR isn't just a digital menu. It's a complete operating system designed to increase sales, streamline operations, and delight guests.
                </p>
            </header>

            {/* FEATURES GRID */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {featuresList.map((feature, idx) => (
                        <div key={idx} className="bg-white p-8 rounded-3xl border border-gray-100 shadow-xl shadow-gray-200/50 hover:-translate-y-1 transition-transform duration-300">
                            <div className="w-14 h-14 bg-gray-50 rounded-2xl flex items-center justify-center text-3xl mb-6 shadow-inner">
                                {feature.icon}
                            </div>
                            <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                            <p className="text-gray-500 leading-relaxed">
                                {feature.description}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA SECTION */}
            <section className="py-20 bg-[#ee5b2b] text-white text-center px-4">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-3xl md:text-5xl font-black mb-6">Ready to upgrade your dining experience?</h2>
                    <p className="text-white/80 text-lg mb-10">Join 500+ restaurants using TableAR to boost sales and efficiency.</p>
                    <button onClick={handlePrimaryAction} className="bg-white text-[#ee5b2b] px-8 py-4 rounded-xl text-lg font-bold shadow-2xl hover:bg-gray-50 transition-colors transform hover:scale-105 active:scale-95">
                        {session ? "Go to Dashboard" : "Get Started for Free"}
                    </button>
                </div>
            </section>

            {/* FOOTER */}
            <footer className="bg-gray-900 text-white py-12 px-4 text-center">
                <div className="flex items-center justify-center gap-2 mb-4 opacity-50">
                    <img src="/favicon.png" alt="Logo" className="w-6 h-6 object-contain grayscale invert" />
                    <span className="font-bold">TableAR</span>
                </div>
                <p className="text-gray-500 text-sm">© {new Date().getFullYear()} ARMenu Inc. All rights reserved.</p>
            </footer>

        </div>
    );
};

export default Features;
